/**
 * FileBrowser — macOS Finder-style column-view file browser with sidebar.
 *
 * Layout:
 *   ┌─────────────┬────────────────────────────────────────────┐
 *   │             │  breadcrumb path bar                       │
 *   │  Sidebar    ├──────────┬──────────┬──────────┬──────────┤
 *   │  - Quick    │ column 0 │ column 1 │ column 2 │ preview  │
 *   │  - Favs     │          │          │          │          │
 *   │  - Recents  │          │          │          │          │
 *   │  - Go to    │          │          │          │          │
 *   └─────────────┴──────────┴──────────┴──────────┴──────────┘
 *
 * Each column shows one directory. Clicking a folder in column N loads
 * its children in column N+1 (columns to the right are cleared).
 * Clicking a file selects it and shows a mini preview in the rightmost area.
 *
 * The sidebar includes quick-access locations, favorites (drag-to-add/reorder),
 * recents, and a "Go to path…" input.
 */

import type { WsFsEntry } from "@magic-md/engine-client/protocol";
import { ChevronRight, Eye, EyeOff, File, Folder, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { workspaceFs } from "../../lib/engine-fs";
import { isPathWithinRoots } from "../../lib/path-utils";
import { cn } from "../../lib/utils";
import { getNotificationBus } from "../../store/notification-bus";
import type { RecentItem } from "../../store/recent-store";
import { BrowserSidebar } from "./BrowserSidebar";
import { addRecent, DRAG_MIME_PATH } from "./file-browser-store";

export interface FileBrowserProps {
    /** Root directory path shown in the first column */
    currentPath: string;
    /** Called when the root directory changes (sidebar / breadcrumb navigation) */
    onNavigate: (path: string) => void;
    /** Called when the user selects an entry */
    onSelect?: (entry: WsFsEntry) => void;
    /** Called when the user double-clicks / confirms a file */
    onFileOpen?: (entry: WsFsEntry) => void;
    /** Called when the sidebar recent workspace shortcut should open directly */
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
    /** "file" = highlight files, "folder" = highlight dirs, "both" = all */
    mode?: "file" | "folder" | "both";
    /** File extension filter (e.g. [".md", ".txt"]) */
    accept?: string[];
    /** Currently selected entry path (for external highlighting) */
    selectedPath?: string;
    /** Path separator (default "/") */
    sep?: string;
}

/* ── Types ── */

/** One column in the column view */
interface Column {
    /** Directory path this column represents */
    path: string;
    /** Loaded entries */
    entries: WsFsEntry[];
    /** Loading state */
    loading: boolean;
    /** Error message */
    error: string | null;
    /** Which entry (by path) is selected/highlighted in this column */
    selectedEntryPath: string | null;
}

/* ── Helpers ── */

function sortEntries(entries: WsFsEntry[]): WsFsEntry[] {
    return [...entries].sort((a, b) => {
        if (a.isDirectory && !b.isDirectory) return -1;
        if (!a.isDirectory && b.isDirectory) return 1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
}

function filterHidden(entries: WsFsEntry[], showHidden: boolean): WsFsEntry[] {
    if (showHidden) return entries;
    return entries.filter((e) => !e.name.startsWith("."));
}

function matchesFilter(name: string, accept?: string[]): boolean {
    if (!accept || accept.length === 0) return true;
    const lower = name.toLowerCase();
    return accept.some((ext) => lower.endsWith(ext.toLowerCase()));
}

function pathSegments(
    fullPath: string,
    sep: string,
    homePath?: string,
): { label: string; path: string }[] {
    const parts = fullPath.split(sep).filter(Boolean);
    const segments: { label: string; path: string }[] = [];
    if (fullPath.startsWith(sep)) {
        segments.push({ label: sep, path: sep });
    }
    let accumulated = fullPath.startsWith(sep) ? sep : "";
    for (const part of parts) {
        accumulated =
            accumulated === sep ? `${sep}${part}` : `${accumulated}${sep}${part}`;
        segments.push({ label: part, path: accumulated });
    }
    // Collapse home prefix into ~
    if (homePath && segments.length > 0) {
        const homeIdx = segments.findIndex((s) => s.path === homePath);
        if (homeIdx >= 0) {
            segments.splice(0, homeIdx + 1, { label: "~", path: homePath });
        }
    }
    return segments;
}

/** Fetch directory contents (returns sorted, unfiltered WsFsEntry[]) */
async function loadDir(path: string): Promise<WsFsEntry[]> {
    const data = await workspaceFs.readDir(path);
    return sortEntries(data);
}

/* ── Column component ── */

function ColumnView({
    column,
    onSelectEntry,
    onDoubleClickEntry,
    mode,
    accept,
    isLast,
    showHidden,
    initialSelectedPath,
}: {
    column: Column;
    onSelectEntry: (entry: WsFsEntry, colPath: string) => void;
    onDoubleClickEntry: (entry: WsFsEntry) => void;
    mode: "file" | "folder" | "both";
    accept?: string[];
    isLast: boolean;
    showHidden: boolean;
    initialSelectedPath?: string;
}) {
    const listRef = useRef<HTMLDivElement>(null);
    const [focusIndex, setFocusIndex] = useState(-1);

    const visibleEntries = useMemo(
        () => filterHidden(column.entries, showHidden),
        [column.entries, showHidden],
    );

    // Reset focus when column changes, but restore if initialSelectedPath matches
    useEffect(() => {
        if (initialSelectedPath) {
            const idx = visibleEntries.findIndex((e) => e.path === initialSelectedPath);
            setFocusIndex(idx >= 0 ? idx : -1);
        } else {
            setFocusIndex(-1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [column.path]);

    // Keyboard navigation
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!visibleEntries.length) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                const next = Math.min(focusIndex + 1, visibleEntries.length - 1);
                setFocusIndex(next);
                onSelectEntry(visibleEntries[next], column.path);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                const next = Math.max(focusIndex - 1, 0);
                setFocusIndex(next);
                onSelectEntry(visibleEntries[next], column.path);
            } else if (e.key === "ArrowRight" || e.key === "Enter") {
                e.preventDefault();
                if (focusIndex >= 0 && focusIndex < visibleEntries.length) {
                    const entry = visibleEntries[focusIndex];
                    if (entry.isDirectory) {
                        onSelectEntry(entry, column.path);
                    } else if (e.key === "Enter") {
                        onDoubleClickEntry(entry);
                    }
                }
            } else if (e.key === "ArrowLeft") {
                // Move focus to the parent column
                const parent = listRef.current
                    ?.previousElementSibling as HTMLElement | null;
                parent?.focus();
            }
        },
        [column, visibleEntries, focusIndex, onSelectEntry, onDoubleClickEntry],
    );

    if (column.loading) {
        return (
            <div
                className={cn(
                    "flex items-center justify-center",
                    isLast ? "flex-1 min-w-[180px]" : "w-[200px] shrink-0",
                )}
            >
                <Loader2 size={16} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (column.error) {
        return (
            <div
                className={cn(
                    "flex items-center justify-center text-[10px] text-destructive/70 px-3 text-center",
                    isLast ? "flex-1 min-w-[180px]" : "w-[200px] shrink-0",
                )}
            >
                {column.error}
            </div>
        );
    }

    return (
        <div
            ref={listRef}
            className={cn(
                "overflow-y-auto border-r border-border/40 focus:outline-none",
                isLast ? "flex-1 min-w-[180px]" : "w-[200px] shrink-0",
            )}
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            {column.entries.length === 0 ? (
                <div className="flex items-center justify-center h-full text-[10px] text-muted-foreground/50">
                    Empty
                </div>
            ) : visibleEntries.length === 0 ? (
                <div className="flex items-center justify-center h-full text-[10px] text-muted-foreground/50">
                    Hidden files only
                </div>
            ) : (
                <div className="py-0.5">
                    {visibleEntries.map((entry, i) => {
                        const isFile = !entry.isDirectory;
                        const dimmed = isFile
                            ? mode === "folder" ||
                            (mode === "file" && !matchesFilter(entry.name, accept))
                            : false;
                        const isSelected = column.selectedEntryPath === entry.path;
                        const isFocused = focusIndex === i;

                        return (
                            <button
                                key={entry.path}
                                type="button"
                                className={cn(
                                    "w-full flex items-center gap-1.5 px-2 py-[3px] text-[11px] text-left transition-colors group",
                                    isSelected || entry.path === initialSelectedPath
                                        ? "bg-primary/15 text-primary"
                                        : isFocused
                                            ? "bg-muted/80"
                                            : "hover:bg-muted/50",
                                    dimmed && "opacity-35",
                                )}
                                onClick={() => {
                                    if (!dimmed) {
                                        setFocusIndex(i);
                                        onSelectEntry(entry, column.path);
                                    }
                                }}
                                onDoubleClick={() => {
                                    if (!dimmed) onDoubleClickEntry(entry);
                                }}
                                disabled={dimmed}
                                draggable={mode !== "folder"}
                                onDragStart={
                                    mode === "folder"
                                        ? undefined
                                        : (e) => {
                                            e.dataTransfer.setData(DRAG_MIME_PATH, entry.path);
                                            e.dataTransfer.setData("text/plain", entry.path);
                                            e.dataTransfer.effectAllowed = "copyMove";
                                        }
                                }
                            >
                                {entry.isDirectory ? (
                                    <Folder size={13} className="shrink-0 text-primary/60" />
                                ) : (
                                    <File
                                        size={13}
                                        className="shrink-0 text-muted-foreground/60"
                                    />
                                )}
                                <span className="truncate flex-1">{entry.name}</span>
                                {entry.isDirectory && (
                                    <ChevronRight
                                        size={10}
                                        className="shrink-0 text-muted-foreground/30 group-hover:text-muted-foreground/60"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ── File preview (shown in rightmost slot when a file is selected) ── */

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function FilePreview({ entry }: { entry: WsFsEntry }) {
    const ext = entry.name.includes(".")
        ? entry.name.substring(entry.name.lastIndexOf("."))
        : "";

    const [stat, setStat] = useState<{
        size: number;
        mtime: number | null;
    } | null>(null);

    useEffect(() => {
        let cancelled = false;
        workspaceFs
            .stat(entry.path)
            .then((s) => {
                if (!cancelled) setStat(s);
            })
            .catch(() => {
                if (!cancelled) setStat(null);
            });
        return () => {
            cancelled = true;
        };
    }, [entry.path]);

    return (
        <div className="flex flex-col items-center justify-center gap-2 text-center p-4 min-w-[160px]">
            <File size={32} className="text-muted-foreground/30" />
            <div className="text-[11px] font-medium text-foreground truncate max-w-full">
                {entry.name}
            </div>
            {ext && (
                <div className="text-[10px] text-muted-foreground/60 uppercase">
                    {ext} file
                </div>
            )}
            {stat && (
                <div className="text-[10px] text-muted-foreground/50 space-y-0.5">
                    <div>{formatSize(stat.size)}</div>
                    {stat.mtime && <div>{new Date(stat.mtime).toLocaleString()}</div>}
                </div>
            )}
        </div>
    );
}

/* ── Main component ── */

export function FileBrowser({
    currentPath,
    onNavigate,
    onSelect,
    onFileOpen,
    onOpenRecentWorkspace,
    mode = "file",
    accept,
    selectedPath: _selectedPath,
    sep = "/",
}: FileBrowserProps) {
    const [columns, setColumns] = useState<Column[]>([]);
    const [selectedFile, setSelectedFile] = useState<WsFsEntry | null>(null);
    const [showHidden, setShowHidden] = useState(false);
    const [homePath, setHomePath] = useState<string>("");
    const [allowedRootPaths, setAllowedRootPaths] = useState<string[] | null>(null);
    const columnsRef = useRef<HTMLDivElement>(null);
    // Monotonically-increasing counter to detect stale directory loads
    const loadIdRef = useRef(0);

    const refreshRuntimeInfo = useCallback(() => {
        workspaceFs
            .pathInfo()
            .then((info) => setResolvedSep(info.sep))
            .catch(() => { });
        workspaceFs
            .home()
            .then(setHomePath)
            .catch(() => setHomePath(""));
        workspaceFs
            .allowedRoots()
            .then((roots) => setAllowedRootPaths(roots.map((root) => root.path)))
            .catch(() => setAllowedRootPaths([]));
    }, []);

    // Track currentPath for sep resolution
    const [resolvedSep, setResolvedSep] = useState(sep);
    useEffect(() => {
        refreshRuntimeInfo();
        const bus = getNotificationBus();
        return bus.subscribe<{ changedKeys?: string[] }>(
            "event/config.changed",
            (params) => {
                if (
                    !params.changedKeys ||
                    params.changedKeys.some((key) => key.startsWith("engine.allowedOperationDirs"))
                ) {
                    refreshRuntimeInfo();
                }
            },
        );
    }, [refreshRuntimeInfo]);

    const effectiveSep = resolvedSep || sep;

    useEffect(() => {
        if (!currentPath || allowedRootPaths === null) return;
        if (allowedRootPaths.length === 0) {
            setColumns([]);
            setSelectedFile(null);
            return;
        }
        if (!isPathWithinRoots(currentPath, allowedRootPaths)) {
            onNavigate(allowedRootPaths[0]);
        }
    }, [allowedRootPaths, currentPath, onNavigate]);

    // Load root column when currentPath changes
    useEffect(() => {
        if (allowedRootPaths === null) return;
        if (!currentPath) return;
        if (
            !isPathWithinRoots(currentPath, allowedRootPaths)
        ) {
            return;
        }

        addRecent(currentPath);
        loadIdRef.current += 1;

        let cancelled = false;
        const rootCol: Column = {
            path: currentPath,
            entries: [],
            loading: true,
            error: null,
            selectedEntryPath: null,
        };
        setColumns([rootCol]);
        setSelectedFile(null);

        loadDir(currentPath)
            .then((entries) => {
                if (!cancelled) {
                    setColumns([{ ...rootCol, entries, loading: false }]);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setColumns([
                        {
                            ...rootCol,
                            loading: false,
                            error: err instanceof Error ? err.message : String(err),
                        },
                    ]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [allowedRootPaths, currentPath]);

    // Auto-scroll columns to the right when new columns appear
    useEffect(() => {
        if (columnsRef.current) {
            columnsRef.current.scrollLeft = columnsRef.current.scrollWidth;
        }
    }, [columns.length]);

    // Handle selecting an entry in a column
    const handleSelectEntry = useCallback(
        (entry: WsFsEntry, colPath: string) => {
            // Update column selection state and determine next columns synchronously
            let nextColumns: Column[] = [];
            setColumns((prev) => {
                const colIndex = prev.findIndex((c) => c.path === colPath);
                if (colIndex === -1) return prev;

                const updated = [...prev];
                updated[colIndex] = {
                    ...updated[colIndex],
                    selectedEntryPath: entry.path,
                };

                const truncated = updated.slice(0, colIndex + 1);

                if (entry.isDirectory) {
                    truncated.push({
                        path: entry.path,
                        entries: [],
                        loading: true,
                        error: null,
                        selectedEntryPath: null,
                    });
                }

                nextColumns = truncated;
                return truncated;
            });

            if (entry.isDirectory) {
                setSelectedFile(null);

                // Load subdirectory outside the state updater to avoid double-invoke
                const loadId = ++loadIdRef.current;
                loadDir(entry.path)
                    .then((entries) => {
                        setColumns((curr) => {
                            if (loadId !== loadIdRef.current) return curr; // stale
                            const idx = curr.findIndex((c) => c.path === entry.path);
                            if (idx === -1) return curr;
                            const next = [...curr];
                            next[idx] = { ...next[idx], entries, loading: false };
                            return next;
                        });
                    })
                    .catch((err) => {
                        setColumns((curr) => {
                            if (loadId !== loadIdRef.current) return curr; // stale
                            const idx = curr.findIndex((c) => c.path === entry.path);
                            if (idx === -1) return curr;
                            const next = [...curr];
                            next[idx] = {
                                ...next[idx],
                                loading: false,
                                error: err instanceof Error ? err.message : String(err),
                            };
                            return next;
                        });
                    });
            } else {
                setSelectedFile(entry);
                void nextColumns; // silence unused warning
            }

            // Notify parent
            if (mode === "folder" && entry.isDirectory) {
                onSelect?.(entry);
            } else if (mode === "file" && !entry.isDirectory) {
                onSelect?.(entry);
            } else if (mode === "both") {
                onSelect?.(entry);
            }
        },
        [mode, onSelect],
    );

    const handleDoubleClickEntry = useCallback(
        (entry: WsFsEntry) => {
            if (entry.isDirectory) {
                // Double-click folder: make it the new root
                onNavigate(entry.path);
            } else {
                onFileOpen?.(entry);
            }
        },
        [onNavigate, onFileOpen],
    );

    // Sidebar navigation — pass onNavigate directly (no redundant wrapper needed)

    const breadcrumbs = useMemo(
        () => pathSegments(currentPath, effectiveSep, homePath || undefined),
        [currentPath, effectiveSep, homePath],
    );

    return (
        <div className="flex h-full">
            {/* Left sidebar */}
            <BrowserSidebar
                onNavigate={onNavigate}
                onOpenRecentWorkspace={onOpenRecentWorkspace}
                currentPath={currentPath}
                sep={effectiveSep}
            />

            {/* Right: breadcrumb + column view */}
            <div className="flex flex-col flex-1 min-w-0">
                {/* Breadcrumb path bar */}
                <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-border bg-muted/20 text-[11px] overflow-x-auto shrink-0">
                    <div className="flex items-center gap-0.5 flex-1 min-w-0">
                        {breadcrumbs.map((seg, i) => (
                            <span
                                key={seg.path}
                                className="flex items-center gap-0.5 whitespace-nowrap"
                            >
                                {i > 0 && (
                                    <ChevronRight
                                        size={9}
                                        className="text-muted-foreground/40 shrink-0"
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => onNavigate(seg.path)}
                                    className={cn(
                                        "px-1 py-0.5 rounded hover:bg-muted transition-colors",
                                        i === breadcrumbs.length - 1
                                            ? "text-foreground font-medium"
                                            : "text-muted-foreground hover:text-foreground",
                                    )}
                                >
                                    {seg.label}
                                </button>
                            </span>
                        ))}
                    </div>
                    {/* Hidden files toggle */}
                    <button
                        type="button"
                        onClick={() => setShowHidden((v) => !v)}
                        title={showHidden ? "Hide hidden files" : "Show hidden files"}
                        className={cn(
                            "shrink-0 p-1 rounded transition-colors",
                            showHidden
                                ? "text-primary bg-primary/10 hover:bg-primary/20"
                                : "text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted",
                        )}
                    >
                        {showHidden ? <Eye size={12} /> : <EyeOff size={12} />}
                    </button>
                </div>

                {/* Column view */}
                <div ref={columnsRef} className="flex flex-1 min-h-0 overflow-x-auto">
                    {columns.map((col, i) => (
                        <ColumnView
                            key={col.path}
                            column={col}
                            onSelectEntry={handleSelectEntry}
                            onDoubleClickEntry={handleDoubleClickEntry}
                            mode={mode}
                            accept={accept}
                            isLast={i === columns.length - 1 && !selectedFile}
                            showHidden={showHidden}
                            initialSelectedPath={_selectedPath}
                        />
                    ))}

                    {/* File preview when a file is selected */}
                    {selectedFile && (
                        <div className="flex-1 min-w-[140px] flex items-center justify-center border-l border-border/40 bg-muted/10">
                            <FilePreview entry={selectedFile} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
